//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useState } from 'react';
import styled from 'styled-components';

import {
  EDIT_TRACKING_PARAMS_PERMISSION,
  OVERRIDE_TRACKING_PARAMS_PERMISSION,
} from '@account-settings/components/tracking-params/TrackingParamConstants';
import {
  CuralateCatalogProduct,
  CuralateProductImage,
  TrackingParam,
  TrackingParamGroup,
} from '@apis/universal-image-actions/universalImageActionsApiModel';
import { PrimaryRouteAnchor } from '@components/RouteAnchor';
import { InvisibleButton } from '@components/atoms/button/Button';
import { ImageWithErrorHandling } from '@components/atoms/image-with-error-handling/ImageWithErrorHandling';
import RequiredPermissionsButton from '@components/atoms/required-permissions-button/RequiredPermissionsButton';
import { BodyText, SectionText } from '@components/atoms/text/Text';
import TextAreaInput from '@components/atoms/text-area-input/TextAreaInput';
import productImagePlaceholder from '@images/acorn-broken.svg';
import { Product } from '@models/IItemMetadata';
import { hasPermissionForTask } from '@services/permissions';
import { Mixins } from '@theme/mixins';
import { UiaProductTrackingIds } from '@uia/products/constants/UiaProductConstants';
import { TrackingParamGroupEditor } from '@uia/products/editor/curalate-product-editor/components/TrackingParamGroupEditor';
import { getProductOrTagImageUrls } from '@uia/products/utils';
import isValidUrl from '@utils/urlValidation';

interface Props {
  isProductEditingFeatureDisabled: boolean;
  onClose: () => void;
  onUpdateUrl: (data: {
    destinationUrl: string;
    paramGroups: TrackingParamGroup[];
  }) => void;
  paramGroups: TrackingParamGroup[];
  product: Product | CuralateCatalogProduct;
  uploadedImage?: CuralateProductImage;
}

export const UrlTrackingParamsEditor = ({
  isProductEditingFeatureDisabled,
  onClose,
  onUpdateUrl,
  paramGroups,
  product,
  uploadedImage,
}: Props) => {
  const [editableParamGroups, setEditableParamGroups] = useState(paramGroups);

  const handleParamChange = (groupId: string, changedParam: TrackingParam) => {
    setEditableParamGroups((currentGroups) =>
      currentGroups.map((group) => {
        if (group.id !== groupId) {
          return group;
        }

        const newParams = group.params.map((param) =>
          param.name === changedParam.name
            ? { ...param, ...changedParam }
            : param,
        );

        return { ...group, params: newParams };
      }),
    );
  };

  const handleUpdateUrl = () =>
    onUpdateUrl({
      destinationUrl: productDestinationUrl,
      paramGroups: editableParamGroups,
    });

  const imageUrls = getProductOrTagImageUrls(product, uploadedImage);
  const hasConfigurePermission = hasPermissionForTask(
    EDIT_TRACKING_PARAMS_PERMISSION,
  );

  const areAnyGroupsInUse = editableParamGroups.some((paramGroup) => {
    return paramGroup.inUse && paramGroup.params.length > 0;
  });

  const [productDestinationUrl, setProductDestinationUrl] = useState(
    product.destinationUrl,
  );

  return (
    <RootContainer>
      <Header>
        <ProductImage
          alt={product.name ?? 'Product image'}
          src={imageUrls[0] ?? productImagePlaceholder}
        />
        <SectionText ellipsize={1}>
          {product.name ?? 'Name not found'}
        </SectionText>
      </Header>
      <CloseButton
        aria-label='Close'
        iconName='close'
        onClick={() => onClose()}
        trackingId={UiaProductTrackingIds.PRODUCT_SECTION_CLOSE_BUTTON}
        type='button'
      />
      <TextAreaInput
        errorText='A valid destination URL is required.'
        hasError={!productDestinationUrl || !isValidUrl(productDestinationUrl)}
        isDisabled={isProductEditingFeatureDisabled}
        label='Base URL'
        onChange={setProductDestinationUrl}
        trackingId={UiaProductTrackingIds.PRODUCT_EDITOR_BASE_URL_TEXT_INPUT}
        value={productDestinationUrl}
      />
      {areAnyGroupsInUse ? (
        <ParamGroupsSection>
          <SectionText>Edit Tracking Parameters</SectionText>
          <BodyText>
            Tracking Parameters are supported in media distributed to Like2Buy,
            Reveal, or Showroom.
          </BodyText>
          {hasConfigurePermission && (
            <PrimaryRouteAnchor
              state='account-settings.tracking'
              target='_blank'
              trackingId={
                UiaProductTrackingIds.PRODUCT_EDITOR_CONFIGURE_TRACKING_PARAMS_LINK
              }
            >
              Configure Tracking Parameters
            </PrimaryRouteAnchor>
          )}
          {editableParamGroups.map((paramGroup) => {
            return (
              <TrackingParamGroupEditor
                key={paramGroup.id}
                onChangeParam={(param) => {
                  handleParamChange(paramGroup.id, param);
                }}
                paramGroup={paramGroup}
              />
            );
          })}
        </ParamGroupsSection>
      ) : (
        <ParamGroupsSection>
          <BodyText>
            Looking to customize your product URLs automatically? Visit the{' '}
            <PrimaryRouteAnchor
              state='account-settings.tracking'
              target='_blank'
              trackingId={
                UiaProductTrackingIds.PRODUCT_EDITOR_CONFIGURE_TRACKING_PARAMS_LINK
              }
            >
              Tracking Parameters
            </PrimaryRouteAnchor>{' '}
            page.
          </BodyText>
        </ParamGroupsSection>
      )}
      <ButtonsContainer>
        <InvisibleButton
          onClick={onClose}
          trackingId={UiaProductTrackingIds.PRODUCT_EDITOR_CANCEL_BUTTON}
        >
          Cancel
        </InvisibleButton>
        <RequiredPermissionsButton
          isDisabled={
            isProductEditingFeatureDisabled ||
            !productDestinationUrl ||
            !isValidUrl(productDestinationUrl)
          }
          onClick={handleUpdateUrl}
          permissionName={OVERRIDE_TRACKING_PARAMS_PERMISSION}
          trackingId={UiaProductTrackingIds.PRODUCT_EDITOR_UPDATE_URL_BUTTON}
        >
          Update URL
        </RequiredPermissionsButton>
      </ButtonsContainer>
    </RootContainer>
  );
};

const RootContainer = styled.div(
  ({ theme }) => `
    ${Mixins.Sheet.Low}
    position: relative;
    flex-direction: column;
    gap: ${theme.spacings[3]};
    padding: ${theme.spacings[3]};
    margin-bottom: ${theme.spacings[7]};
  `,
);

const Header = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: row;
    gap: ${theme.spacings[2]};
  `,
);

const ProductImage = styled(ImageWithErrorHandling)(
  ({ theme }) => `
  width: calc(${theme.spacings[5]} + ${theme.spacings[1]});
  aspect-ratio: 1;
  object-fit: contain;
`,
);

const ParamGroupsSection = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[3]};
    margin-top: ${theme.spacings[2]};
  `,
);

const ButtonsContainer = styled.div(
  ({ theme }) => `
    display: flex;
    justify-content: end;
    gap: ${theme.spacings[3]};
  `,
);

const CloseButton = styled(InvisibleButton)`
  position: absolute;
  top: 0;
  right: 0;
`;
